import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const NotificationContext = createContext();

// Notification types
export const NOTIFICATION_TYPES = {
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info'
};

export function NotificationProvider({ children }) {
    const [notifications, setNotifications] = useState([]);
    const [browserPermission, setBrowserPermission] = useState('default');

    // Check browser notification permission on mount
    useEffect(() => {
        if ('Notification' in window) {
            setBrowserPermission(Notification.permission);
        }
    }, []);

    // Request browser notification permission
    const requestPermission = useCallback(async () => {
        if (!('Notification' in window)) {
            console.warn('This browser does not support notifications');
            return false;
        }

        try {
            const permission = await Notification.requestPermission();
            setBrowserPermission(permission);
            return permission === 'granted';
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    }, []);

    // Remove notification
    const removeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    // Add notification
    const addNotification = useCallback((notification) => {
        const id = Date.now() + Math.random().toString(36).substr(2, 9);
        const newNotification = {
            id,
            type: notification.type || NOTIFICATION_TYPES.INFO,
            title: notification.title || '',
            message: notification.message || '',
            duration: notification.duration || 5000,
            persistent: notification.persistent || false,
            action: notification.action || null,
            createdAt: new Date().toISOString()
        };

        setNotifications(prev => [...prev, newNotification]);

        // Auto-remove after duration (if not persistent)
        if (!newNotification.persistent && newNotification.duration > 0) {
            setTimeout(() => {
                removeNotification(id);
            }, newNotification.duration);
        }

        return id;
    }, [removeNotification]);

    // Clear all notifications
    const clearAllNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    // Show browser notification
    const showBrowserNotification = useCallback((title, options = {}) => {
        if (!('Notification' in window)) return;

        if (Notification.permission === 'granted') {
            const notification = new Notification(title, {
                icon: '/favicon.ico',
                badge: '/favicon.ico',
                tag: options.tag || 'getiri-notification',
                ...options
            });

            notification.onclick = () => {
                window.focus();
                if (options.onClick) options.onClick();
                notification.close();
            };

            return notification;
        }
    }, []);

    // Convenience methods for different notification types
    const notify = {
        success: useCallback((title, message, options = {}) => {
            return addNotification({ type: NOTIFICATION_TYPES.SUCCESS, title, message, ...options });
        }, [addNotification]),

        error: useCallback((title, message, options = {}) => {
            return addNotification({ type: NOTIFICATION_TYPES.ERROR, title, message, duration: 8000, ...options });
        }, [addNotification]),

        warning: useCallback((title, message, options = {}) => {
            return addNotification({ type: NOTIFICATION_TYPES.WARNING, title, message, ...options });
        }, [addNotification]),

        info: useCallback((title, message, options = {}) => {
            return addNotification({ type: NOTIFICATION_TYPES.INFO, title, message, ...options });
        }, [addNotification])
    };

    // Price alert functionality
    const [priceAlerts, setPriceAlerts] = useState(() => {
        try {
            const saved = localStorage.getItem('getiri_price_alerts');
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem('getiri_price_alerts', JSON.stringify(priceAlerts));
    }, [priceAlerts]);

    const addPriceAlert = useCallback((alert) => {
        const id = Date.now().toString();
        const newAlert = {
            id,
            pair: alert.pair,
            condition: alert.condition, // 'above' or 'below'
            targetPrice: alert.targetPrice,
            active: true,
            createdAt: new Date().toISOString()
        };
        setPriceAlerts(prev => [...prev, newAlert]);
        return id;
    }, []);

    const removePriceAlert = useCallback((id) => {
        setPriceAlerts(prev => prev.filter(a => a.id !== id));
    }, []);

    const togglePriceAlert = useCallback((id) => {
        setPriceAlerts(prev => prev.map(a =>
            a.id === id ? { ...a, active: !a.active } : a
        ));
    }, []);

    const value = {
        notifications,
        addNotification,
        removeNotification,
        clearAllNotifications,
        notify,
        browserPermission,
        requestPermission,
        showBrowserNotification,
        priceAlerts,
        addPriceAlert,
        removePriceAlert,
        togglePriceAlert
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotification() {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
}
